import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { ApiGeometryType, LatLong, MapLayer } from '../../models/Geometries';
import { fetchAllLayers, fetchFeatureAttributes, fetchFeaturesInArea } from '../services/map/map.service';
import { LngLatBounds } from 'mapbox-gl';
import { Feature } from '../../models/Geoserver';
import { loadGeometries } from './mapSlice';
import { GeometryService } from '../services/map/geometry.service';
import { message } from 'antd';
import { generateBounds } from '../../utils/GeometryUtils';

export type FeatureSelectedMode = 'view-attributes' | 'add' | 'remove';

export interface MapLayersState {
  error?: string;
  layers: MapLayer[];
  allLayers: MapLayer[];
  featureSelected?: Feature;
  featureRemoved?: Feature;
  featureSelectedMode: FeatureSelectedMode;
  taskFeatures: Feature[];
  //seleccion multiple:
  addingMultipleFeatures: boolean;
  firstPoint?: LatLong;
  secondPoint?: LatLong;
  preSelectedFeatures: Feature[];
  loading: boolean;
  multipleSelectionTutorial: boolean;
  loadingMultipleSelection: boolean;
  addGeometriesTutorial: boolean;
}

const initialState: MapLayersState = {
  layers: [],
  allLayers: [],
  taskFeatures: [],
  featureSelectedMode: 'view-attributes',
  addingMultipleFeatures: false,
  preSelectedFeatures: [],
  loading: false,
  multipleSelectionTutorial: false,
  loadingMultipleSelection: false,
  addGeometriesTutorial: false,
};

export const mapLayerslice = createSlice({
  name: 'mapLayers',
  initialState,
  reducers: {
    resetLayers: (state) => {
      state.taskFeatures = [];
      state.layers = [];
      state.addingMultipleFeatures = false;
      state.firstPoint = undefined;
      state.secondPoint = undefined;
      state.preSelectedFeatures = [];
      state.loading = false;
      state.loadingMultipleSelection = false;
      state.featureSelected = undefined;
    },
    closeMultipleSelectionTutorial: state => {
      state.multipleSelectionTutorial = false;
    },
    closeAddGeometriesTutorial: state => {
      state.addGeometriesTutorial = false;
    },
    setLayersList: (state: MapLayersState, arg: PayloadAction<MapLayer[]>) => {
      state.layers = arg.payload;
      if (state.layers.some(el => el.type === process.env.REACT_APP_AOI_GEOSERVER_LAYER_EXCLUDED)) {
        state.addGeometriesTutorial = !localStorage.getItem('addGeometriesTutorialShown');
      }
    },
    unselectFeature: (state: MapLayersState) => {
      state.featureSelected = undefined;
    },
    selectFeature: (state: MapLayersState, arg: PayloadAction<{ featureId: string, mode: FeatureSelectedMode }>) => {
      state.featureSelected = state.taskFeatures.find(f => f.id == arg.payload.featureId);
      state.featureSelectedMode = arg.payload.mode;
    },
    clearFeatureRemoved: (state: MapLayersState) => {
      state.featureRemoved = undefined;
    },
    setAddingMultipleFeatures: (state, arg: PayloadAction<boolean>) => {
      state.addingMultipleFeatures = arg.payload;
      if(state.addingMultipleFeatures){
        state.multipleSelectionTutorial = !localStorage.getItem('multipleFeaturesTutorialShown');
      }
      state.firstPoint = undefined;
      state.secondPoint = undefined;
      state.preSelectedFeatures = [];
      state.loading = false;
    },
    unselectPreselectedFeature: (state, arg: PayloadAction<any>) => {
      state.preSelectedFeatures = state.preSelectedFeatures.filter(f => f.properties._featureId !== arg.payload);
      if (state.preSelectedFeatures.length === 0) {
        state.addingMultipleFeatures = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllLayers.fulfilled, (state, action) => {
      state.allLayers = action.payload;
    });
    builder.addCase(getAllLayers.rejected, (state, action) => {
      console.error(action.error);
      message.error('Error al recuperar capas del Catastro Minero.');
    });
    builder.addCase(showLayerAttributes.fulfilled, (state, action) => {
      for (const payload of action.payload) {
        if (payload?.features?.length && payload.features[0].geometry) {
          const feature = payload.features[0];
          feature.properties._featureId = feature.id;
          feature.properties.layer = payload.layer.name;
          // Si estamos agregando multiples geometrias, cuando se hace click en una geometria del cm, se agrega a las
          // seleccionadas, en lugar de mostrar los atributos
          if (state.addingMultipleFeatures) {
            if (!state.preSelectedFeatures.find(f => f.id === feature.id) && !state.loading) {
              state.preSelectedFeatures.push(feature);
            }
          } else {
            if (state.featureSelectedMode === 'add' && state.taskFeatures.some(f => f.id === feature.id)) {
              state.featureSelectedMode = 'remove';
            }
            state.featureSelected = feature;
            state.featureSelected.layer = payload.layer.name;
          }
          break;
        }
      }
    });
    builder.addCase(showLayerAttributes.pending, (state, action) => {
      state.featureSelectedMode = action.meta.arg.click === 'right' ? 'add' : 'view-attributes';
    });
    builder.addCase(showLayerAttributes.rejected, (state, action) => {
      console.error(JSON.stringify(action.error));
    });
    builder.addCase(loadGeometries.fulfilled, (state, action) => {
      state.taskFeatures = [];
      action.payload.forEach(apiGeometry => {
        if (apiGeometry.tipo === ApiGeometryType.CM) {
          state.taskFeatures.push({
            id: apiGeometry.id,
            geometry: apiGeometry.geometria,
            properties: { ...apiGeometry.atributos, _featureId: apiGeometry.id },
            atriutosRegistros: apiGeometry.atributos.atributosRegistros ?? {},
            layer: apiGeometry.atributos.layer,
            type: apiGeometry.atributos.type,
          });
        }
      });
    });
    builder.addCase(addFeatureSelected.fulfilled, (state, action) => {
      if(state.featureSelected){
        state.featureSelected.id = action.payload.id;
        state.featureSelected.properties._featureId = state.featureSelected.id;
        state.taskFeatures.push(state.featureSelected);
        state.featureSelected = undefined;
      }
    });
    builder.addCase(removeFeatureSelected.fulfilled, (state, action) => {
      const index = state.taskFeatures.findIndex(p => p.id === state.featureSelected!.id);
      state.featureRemoved = state.taskFeatures[index];
      state.featureSelected = undefined;
      state.taskFeatures.splice(index, 1);
    });
    builder.addCase(addMultiplesFeaturesPoint.pending, (state, action) => {
      if (state.firstPoint === undefined) {
        state.firstPoint = action.meta.arg.coords;
      } else {
        state.secondPoint = action.meta.arg.coords;
        state.loadingMultipleSelection = true;
      }
    });
    builder.addCase(addMultiplesFeaturesPoint.fulfilled, (state, action) => {
      if (!action.payload || state.loading || !state.secondPoint) {
        return;
      }
      let totalFeatures = 0;
      for (const payload of action.payload) {
        if (payload?.features?.length) {
          totalFeatures += payload.features.length;
          state.preSelectedFeatures = [
            ...state.preSelectedFeatures,
            ...payload.features
              .map(f => {
                f.properties._featureId = f.id;
                f.properties.layer = payload.layer.name;
                return f;
              })
              //excluyo las que ya estan agregadas en la tarea
              .filter(f => !state.taskFeatures.find(tf => tf.properties._featureId === f.id)),
          ];
        }
      }
      // si no selecciona ninguna geometria, salimos del modo de seleccion multiple
      if(totalFeatures === 0){
        state.addingMultipleFeatures = false;
      }
      state.loadingMultipleSelection = false;
    });
    builder.addCase(addMultipleFeaturesSelected.pending, state => {
      state.loading = true;
    })
    builder.addCase(addMultipleFeaturesSelected.rejected, (state, action) => {
      state.loading = false;
      console.error(action.error);
      message.error('Error al guardar geometrias.');
    })
    builder.addCase(addMultipleFeaturesSelected.fulfilled, (state, action) => {
      state.loading = false;
      action.payload.forEach(apiGeometry => {
        state.taskFeatures.push({
          id: apiGeometry.id,
          geometry: apiGeometry.geometria,
          properties: { ...apiGeometry.atributos, _featureId: apiGeometry.id },
          atriutosRegistros: apiGeometry.atributos.atributosRegistros,
          layer: apiGeometry.atributos.layer,
          type: apiGeometry.atributos.type,
        });
      });
      state.preSelectedFeatures = [];
      state.addingMultipleFeatures = false;

    })
  },
});

export const getAllLayers = createAsyncThunk('all_layers', async () => {
  return fetchAllLayers();
});

export const showLayerAttributes = createAsyncThunk(
  'layer_attributes',
  async (
    payload: { coords: LatLong; mapBounds: LngLatBounds; mapWidth: number, click: 'left' | 'right' },
    thunkApi,
  ) => {
    // calculo un area de busqueda proporcional al area del mapa en pantalla
    // para que no sea necesario clickear el pixel exacto de una figura (por si son tipo punto)
    const longitudeDelta = Math.abs(
      payload.mapBounds.getNorthWest().lng -
      payload.mapBounds.getNorthEast().lng,
    );
    //Con la diferencia de longitudes del mapa y el ancho en pixeles, calculo
    //cuanto es un pixel en longitud
    const coordsClickableDelta = (longitudeDelta / payload.mapWidth);
    const swBound: LatLong = {
      latitud: payload.coords.latitud - coordsClickableDelta,
      longitud: payload.coords.longitud - coordsClickableDelta,
    };
    const neBound: LatLong = {
      latitud: payload.coords.latitud + coordsClickableDelta,
      longitud: payload.coords.longitud + coordsClickableDelta,
    };
    let layers: MapLayer[] = (thunkApi.getState() as RootState).mapLayers.layers;
    //Solo voy a buscar en capas del catastro minero
    layers = layers.filter(l => l.type === process.env.REACT_APP_AOI_GEOSERVER_LAYER_EXCLUDED);
    return Promise.all(layers.map(l => fetchFeatureAttributes(l, swBound, neBound, true)));
  },
);

export const addFeatureSelected = createAsyncThunk('add_feature_selected', async (_: void, thunkApi) => {
  const state: MapLayersState = (thunkApi.getState() as RootState).mapLayers;
  return await GeometryService.saveGeometry({
    titulo: state.featureSelected!.properties.nombre ?? state.featureSelected!.id,
    tipo: ApiGeometryType.CM,
    atributos: {
      ...state.featureSelected!.properties,
      layer: state.featureSelected!.layer,
      type: state.featureSelected!.type,
      atributosRegistros: state.featureSelected!.atriutosRegistros
    },
    geometria: state.featureSelected!.geometry,
    tareaId: (thunkApi.getState() as RootState).task.currentTask!.id!,
  });
});

export const addMultipleFeaturesSelected = createAsyncThunk('addMultipleFeaturesSelected', async (_: void, thunkApi) => {
  const state: MapLayersState = (thunkApi.getState() as RootState).mapLayers;
  return await Promise.all(
    state.preSelectedFeatures.map(f => GeometryService.saveGeometry({
      titulo: f.properties.nombre ?? f.id,
      tipo: ApiGeometryType.CM,
      atributos: {
        ...f.properties,
        type: f.type,
        atributosRegistros: f.atriutosRegistros
      },
      geometria: f.geometry,
      tareaId: (thunkApi.getState() as RootState).task.currentTask!.id!,
    }))
  )
});

export const removeFeatureSelected = createAsyncThunk('remove_feature_selected', async (_: void, thunkApi) => {
  const state: MapLayersState = (thunkApi.getState() as RootState).mapLayers;
  return await GeometryService.deleteGeometry(state.featureSelected!.id);
});

export const addMultiplesFeaturesPoint = createAsyncThunk(
  'addMultiplesFeaturesPoint',
  async (payload: { coords: LatLong }, { getState }) => {
    const state = (getState() as RootState);
    if (state.mapLayers.firstPoint) {
      const layers = state.mapLayers.layers.filter(l => l.type === process.env.REACT_APP_AOI_GEOSERVER_LAYER_EXCLUDED);
      const secondPoint = payload.coords;
      const bounds = generateBounds(state.mapLayers.firstPoint, secondPoint);
      const swBound: LatLong = {
        longitud: bounds[0],
        latitud: bounds[1],
      };
      const neBound: LatLong = {
        longitud: bounds[2],
        latitud: bounds[3],
      };
      return Promise.all(layers.map(l => fetchFeaturesInArea(l, swBound, neBound, true)));
    }
  });

export const {
  setLayersList,
  unselectFeature,
  selectFeature,
  clearFeatureRemoved,
  resetLayers,
  setAddingMultipleFeatures,
  unselectPreselectedFeature,
  closeMultipleSelectionTutorial,
  closeAddGeometriesTutorial,
} = mapLayerslice.actions;

export const selectLayers = (state: RootState) => state.mapLayers.layers;
export const selectFeatureSelected = (state: RootState) =>
  state.mapLayers.featureSelected;
export const selectFeatureRemoved = (state: RootState) =>
  state.mapLayers.featureRemoved;
export const selectFeatureSelectedMode = (state: RootState) =>
  state.mapLayers.featureSelectedMode;
export const selectTaskFeatures = (state: RootState) =>
  state.mapLayers.taskFeatures;
export const selectMapLayersError = (state: RootState) => state.mapLayers.error;
export const selectAllLayers = (state: RootState) => state.mapLayers.allLayers;
export default mapLayerslice.reducer;
